import React, { useEffect } from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider, EmotionCache } from '@emotion/react';
import createCache from '@emotion/cache';
import '@fontsource/inter';
import '../styles/global.css';
import '../styles/google-button.css';
import '../styles/editor.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar-dark-mode.css';
import 'react-calendar/dist/Calendar.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { SessionProvider } from '../components/SessionContext';
import { ThemeProvider } from '../lib/ThemeContext';
import { ensureCsrfToken } from '../lib/api/public';

import Notifier from '../components/common/Notifier';
import Confirmer from '../components/common/Confirmer';

const MyApp = ({ Component, pageProps, emotionCache }: AppProps & { emotionCache?: EmotionCache }) => {
  const clientEmotionCache = React.useMemo(() => {
    const nonce =
      typeof document !== 'undefined'
        ? (document.querySelector('meta[name="csp-nonce"]') as HTMLMetaElement | null)?.content
        : undefined;
    return createCache({ key: 'css', nonce: nonce || undefined });
  }, []);

  const effectiveEmotionCache = emotionCache ?? clientEmotionCache;
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      if (hostname.includes('signup.') && router.pathname !== '/signup') {
        void router.push('/signup');
      }
    }
  }, [router]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    void ensureCsrfToken();
  }, []);

  return (
    <CacheProvider value={effectiveEmotionCache}>
      <Head>
        <title>Harbor</title>
      </Head>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <SessionProvider>
          <ThemeProvider>
            <CssBaseline />
            <Component {...pageProps} />

            <Notifier />
            <Confirmer />
          </ThemeProvider>
        </SessionProvider>
      </LocalizationProvider>
    </CacheProvider>
  );
};

export default MyApp;
